<template>
  <div class="page-content">
    <div class="search-bar">
      <div class="search-input-wrapper">
        <i class="el-icon-search search-icon"></i>
        <input
          v-model="searchText"
          type="text"
          class="search-input"
          placeholder="搜索用户名/昵称/手机号..."
          @keyup.enter="handleSearch"
        />
      </div>
      <div class="action-buttons">
        <button class="btn btn-secondary">
          <i class="el-icon-filter"></i>
          筛选
        </button>
        <button class="btn btn-primary">
          <i class="el-icon-plus"></i>
          新增用户
        </button>
      </div>
    </div>

    <div class="card">
      <div class="table-wrapper">
        <table class="data-table">
          <thead>
            <tr>
              <th>用户ID</th>
              <th>用户信息</th>
              <th>联系方式</th>
              <th>权限</th>
              <th>状态</th>
              <th>最后活跃</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in users" :key="user.id" class="table-row">
              <td class="id-cell">#{{ user.id }}</td>
              <td>
                <div class="user-info">
                  <div class="user-avatar">
                    <img v-if="user.headImage" :src="user.headImage" alt="avatar" />
                    <i v-else class="el-icon-user-solid"></i>
                  </div>
                  <div class="user-detail">
                    <span class="username">{{ user.userName }}</span>
                    <span class="nickname">{{ user.nickName }}</span>
                  </div>
                </div>
              </td>
              <td>
                <div class="contact-info">
                  <span class="phone">{{ user.phone || '-' }}</span>
                  <span class="email">{{ user.email || '-' }}</span>
                </div>
              </td>
              <td>
                <span class="badge" :class="user.isAdmin ? 'badge-admin' : 'badge-normal'">
                  {{ user.isAdmin ? '管理员' : '普通用户' }}
                </span>
              </td>
              <td>
                <div class="status-cell" :class="user.isBanned ? 'status-blocked' : 'status-active'">
                  <span class="status-dot"></span>
                  <span class="status-text">{{ user.isBanned ? '封禁' : '正常' }}</span>
                </div>
              </td>
              <td>
                <div class="activity-cell">
                  <span class="time">{{ user.lastLoginTime || '-' }}</span>
                  <span class="ip">{{ user.lastLoginIp || '-' }}</span>
                </div>
              </td>
              <td>
                <div class="action-cell">
                  <button
                    class="action-btn"
                    :class="user.isAdmin ? 'action-danger' : 'action-primary'"
                    :title="user.isAdmin ? '移除管理员' : '设为管理员'"
                    @click="toggleAdmin(user)"
                  >
                    <i :class="user.isAdmin ? 'ri-shield-off-line' : 'ri-shield-check-line'"></i>
                  </button>
                  <button
                    class="action-btn"
                    :class="user.isBanned ? 'action-success' : 'action-danger'"
                    :title="user.isBanned ? '解封' : '封禁'"
                    @click="toggleBlock(user)"
                  >
                    <i :class="user.isBanned ? 'ri-lock-unlock-line' : 'ri-forbid-line'"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="pagination-bar">
        <span class="pagination-info">显示 {{ (currentPage - 1) * pageSize + 1 }} 到 {{ Math.min(currentPage * pageSize, total) }} 条，共 {{ total }} 条</span>
        <div class="pagination">
          <button class="page-btn" :disabled="currentPage === 1" @click="currentPage--">上一页</button>
          <button
            v-for="page in visiblePages"
            :key="page"
            class="page-btn"
            :class="{ active: page === currentPage, ellipsis: page === '...' }"
            :disabled="page === '...'"
            @click="page !== '...' && (currentPage = page)"
          >
            {{ page }}
          </button>
          <button class="page-btn" :disabled="currentPage === totalPages" @click="currentPage++">下一页</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'UserManagement',
  data() {
    return {
      users: [],
      searchText: '',
      currentPage: 1,
      pageSize: 10,
      total: 0
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.total / this.pageSize)
    },
    visiblePages() {
      const pages = []
      const total = this.totalPages
      const current = this.currentPage
      if (total <= 5) {
        for (let i = 1; i <= total; i++) pages.push(i)
      } else {
        if (current <= 3) {
          pages.push(1, 2, 3, 4, '...', total)
        } else if (current >= total - 2) {
          pages.push(1, '...', total - 3, total - 2, total - 1, total)
        } else {
          pages.push(1, '...', current - 1, current, current + 1, '...', total)
        }
      }
      return pages
    }
  },
  mounted() {
    this.loadUsers()
  },
  watch: {
    currentPage() {
      this.loadUsers()
    }
  },
  methods: {
    async loadUsers() {
      try {
        const response = await this.$http({
          url: '/admin/user/list',
          method: 'get',
          params: {
            page: this.currentPage,
            size: this.pageSize,
            keyword: this.searchText
          }
        })
        this.users = response.list || response
        this.total = response.total || this.users.length
      } catch (error) {
        this.$message.error('加载用户列表失败')
      }
    },
    handleSearch() {
      this.currentPage = 1
      this.loadUsers()
    },
    async toggleAdmin(user) {
      const action = user.isAdmin ? 'remove-admin' : 'set-admin'
      try {
        await this.$http({
          url: `/admin/user/${action}/${user.id}`,
          method: 'post'
        })
        user.isAdmin = !user.isAdmin
        this.$message.success(user.isAdmin ? '已设为管理员' : '已移除管理员权限')
      } catch (error) {
        this.$message.error('操作失败')
      }
    },
    async toggleBlock(user) {
      const action = user.isBanned ? 'unblock' : 'block'
      try {
        await this.$http({
          url: `/admin/user/${action}/${user.id}`,
          method: 'post'
        })
        user.isBanned = !user.isBanned
        this.$message.success(user.isBanned ? '已封禁用户' : '已解封用户')
      } catch (error) {
        this.$message.error('操作失败')
      }
    }
  }
}
</script>

<style scoped>
@import url('https://cdn.jsdelivr.net/npm/remixicon@4.2.0/fonts/remixicon.css');

.page-content {
  max-width: 100%;
}

.search-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  gap: 16px;
}

.search-input-wrapper {
  position: relative;
  max-width: 320px;
  width: 100%;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 16px;
}

.search-input {
  width: 100%;
  padding: 10px 12px 10px 40px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  transition: all 0.2s;
  background: #fff;
}

.search-input:focus {
  border-color: #07c160;
  box-shadow: 0 0 0 3px rgba(7, 193, 96, 0.1);
}

.search-input::placeholder {
  color: #94a3b8;
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.btn i {
  font-size: 16px;
}

.btn-secondary {
  background: #fff;
  border: 1px solid #e2e8f0;
  color: #64748b;
}

.btn-secondary:hover {
  background: #f8fafc;
  border-color: #07c160;
  color: #07c160;
}

.btn-primary {
  background: #07c160;
  color: #fff;
  box-shadow: 0 2px 8px rgba(7, 193, 96, 0.25);
}

.btn-primary:hover {
  background: #06ae56;
  box-shadow: 0 4px 12px rgba(7, 193, 96, 0.35);
}

.card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

.table-wrapper {
  overflow-x: auto;
  max-height: calc(100vh - 380px);
  overflow-y: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
}

.data-table thead {
  background: #f8fafc;
}

.data-table th {
  padding: 16px 24px;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #e2e8f0;
}

.data-table td {
  padding: 16px 24px;
  border-bottom: 1px solid #f1f5f9;
  font-size: 14px;
  color: #334155;
}

.table-row:hover {
  background: #f9fafb;
}

.id-cell {
  color: #94a3b8;
  font-weight: 500;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  color: #64748b;
  font-size: 18px;
  flex-shrink: 0;
}

.user-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.user-detail {
  display: flex;
  flex-direction: column;
}

.username {
  font-weight: 600;
  color: #0f172a;
}

.nickname {
  font-size: 12px;
  color: #94a3b8;
}

.contact-info {
  display: flex;
  flex-direction: column;
}

.phone {
  color: #334155;
}

.email {
  font-size: 12px;
  color: #94a3b8;
}

.badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 500;
}

.badge-admin {
  background: #ecfdf5;
  color: #059669;
}

.badge-normal {
  background: #f1f5f9;
  color: #64748b;
}

.status-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.status-active .status-dot {
  background: #10b981;
}

.status-active .status-text {
  color: #059669;
}

.status-blocked .status-dot {
  background: #ef4444;
}

.status-blocked .status-text {
  color: #dc2626;
}

.activity-cell {
  display: flex;
  flex-direction: column;
}

.time {
  color: #334155;
}

.ip {
  font-size: 12px;
  color: #94a3b8;
}

.action-cell {
  display: flex;
  gap: 8px;
}

.action-btn {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.action-btn i {
  font-size: 18px;
}

.action-btn:hover {
  background: #f1f5f9;
}

.action-primary:hover {
  color: #07c160;
}

.action-danger:hover {
  color: #ef4444;
}

.action-success:hover {
  color: #10b981;
}

.pagination-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
}

.pagination-info {
  font-size: 14px;
  color: #64748b;
}

.pagination {
  display: flex;
  gap: 4px;
}

.page-btn {
  min-width: 32px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #fff;
  color: #64748b;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.page-btn:hover:not(:disabled) {
  border-color: #07c160;
  color: #07c160;
}

.page-btn.active {
  background: #07c160;
  border-color: #07c160;
  color: #fff;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-btn.ellipsis {
  border: none;
  background: transparent;
  cursor: default;
}
</style>